// ZONE 2 · MOMENTUM — how it has been going.
//
// One bar per week from first contact to today, bar height = documents + events that week,
// plus three figures: days since last contact, longest silence, events on the record.
//
// SUPPRESSION IS A RULE, and the server owns it: below 8 weeks or 5 touch days the payload
// ships `weeks: []`, `suppressed: true` and the sentence that says why. A shape drawn from
// four data points is noise wearing a chart's clothes, and the page does not draw one.
import type { BriefPayload } from '../api/brief.js';
import { useT, useLocale } from '../i18n/index.js';
import './ClientMomentum.de.js';
import './ClientMomentum.fr.js';

type Momentum = BriefPayload['momentum'];

interface Props {
  momentum: Momentum;
  /** Already formatted by the rail's own DAY detail — this zone never re-derives a date. */
  firstContactLabel: string | null;
}

export default function ClientMomentum({ momentum, firstContactLabel }: Props) {
  const t = useT();
  const { fmtDate } = useLocale();
  const weeks = momentum.weeks;
  const peak = weeks.reduce((n, w) => Math.max(n, w.docs + w.events), 0);

  return (
    <section className="zone" id="momentum">
      <div className="zone-head">
        <span className="u-label">{t('Momentum')}</span>
        <span className="zone-count voice-figure">
          {firstContactLabel ? t('weekly contact since {date}', { date: firstContactLabel }) : t('no dated evidence')}
        </span>
      </div>

      {momentum.suppressed ? (
        <p className="zone-empty">
          {momentum.note} {t('The strip appears once there are 5 touches over 8 weeks.')}
        </p>
      ) : (
        <div className="sheet sheet--pad client-mom">
          <div className="client-mom-chart">
            <div className="client-bars">
              {weeks.map((w, i) => {
                const n = w.docs + w.events;
                const height = n ? Math.max(10, Math.round((n / Math.max(peak, 1)) * 64)) : 2;
                const last = i === weeks.length - 1;
                const cls = !n ? ' client-bar--gap' : last ? ' client-bar--now' : '';
                return (
                  <div
                    key={w.week_start}
                    className={`client-bar${cls}`}
                    style={{ height }}
                    title={t('{date} · {docs} documents, {events} events', { date: fmtDate(w.week_start), docs: w.docs, events: w.events })}
                  />
                );
              })}
            </div>
            <div className="client-axis voice-figure">
              <span>{fmtDate(weeks[0]?.week_start ?? null)}</span>
              <span>{t('{n} weeks', { n: momentum.weeks_count })}</span>
              <span>{fmtDate(weeks[weeks.length - 1]?.week_start ?? null)}</span>
            </div>
            <div className="client-legend">
              <span>{t('bar height = documents + events that week')}</span>
              <span style={{ marginLeft: 'auto' }}>{t('{n} days of contact', { n: momentum.touches })}</span>
            </div>
          </div>

          <div className="client-mom-read">
            <div>
              <div className="client-mom-n voice-figure">{momentum.days_since_last_contact ?? '—'}</div>
              <div className="client-mom-l">{t('days since last contact')}</div>
            </div>
            <div>
              <div className={`client-mom-n voice-figure${(momentum.longest_silence_days ?? 0) > 30 ? ' client-mom-n--warn' : ''}`}>
                {momentum.longest_silence_days ?? '—'}
              </div>
              <div className="client-mom-l">{t('longest silence, in days')}</div>
            </div>
            <div>
              <div className="client-mom-n voice-figure">{momentum.events_on_record}</div>
              <div className="client-mom-l">{t('events on the record')}</div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
